import React, { useMemo, useState } from 'react';
import {
	Activity,
	Globe,
	Server,
	Database,
	HardDrive,
	CreditCard,
	MessageSquare,
	Printer,
	Mail,
	Brain,
	MapPin,
	Layout,
	Shield,
} from 'lucide-react';
import { Heading } from '../catalyst/heading';
import { Text } from '../catalyst/text';
import { Badge } from '../catalyst/badge';
import {
	useHealthChecksCurrent,
	useHealthChecksHistory,
} from '../hooks/useHealthChecks';
import type { ServiceStats } from '../hooks/useHealthChecks';
import type { HealthCheck, HealthStatus } from '../types/database';

// ---------------------------------------------------------------------------
// Service display metadata
// ---------------------------------------------------------------------------

const SERVICE_META: Record<string, { label: string; icon: React.ReactNode }> = {
	api_develop: { label: 'API (Develop)', icon: <Server className="h-4 w-4" /> },
	api_production: { label: 'API (Production)', icon: <Server className="h-4 w-4" /> },
	supabase: { label: 'Supabase', icon: <Database className="h-4 w-4" /> },
	s3_storage: { label: 'S3 Storage', icon: <HardDrive className="h-4 w-4" /> },
	stripe: { label: 'Stripe', icon: <CreditCard className="h-4 w-4" /> },
	kapso: { label: 'Kapso (WhatsApp)', icon: <MessageSquare className="h-4 w-4" /> },
	prodigi: { label: 'Prodigi', icon: <Printer className="h-4 w-4" /> },
	lob: { label: 'Lob', icon: <Mail className="h-4 w-4" /> },
	openai: { label: 'OpenAI', icon: <Brain className="h-4 w-4" /> },
	google_places: { label: 'Google Places', icon: <MapPin className="h-4 w-4" /> },
	dashboard: { label: 'Dashboard', icon: <Layout className="h-4 w-4" /> },
	landing_page: { label: 'Landing Page', icon: <Globe className="h-4 w-4" /> },
	sentry: { label: 'Sentry', icon: <Shield className="h-4 w-4" /> },
};

const STATUS_DOT: Record<HealthStatus, string> = {
	healthy: 'bg-emerald-500',
	degraded: 'bg-amber-500',
	down: 'bg-red-500',
};

const STATUS_LABEL: Record<HealthStatus, string> = {
	healthy: 'Operational',
	degraded: 'Degraded',
	down: 'Down',
};

const STATUS_BADGE_COLOR: Record<HealthStatus, string> = {
	healthy: 'green',
	degraded: 'amber',
	down: 'red',
};

// Ordered list of services — controls display order
const SERVICE_ORDER = [
	'api_develop',
	'api_production',
	'supabase',
	's3_storage',
	'stripe',
	'kapso',
	'prodigi',
	'lob',
	'openai',
	'google_places',
	'dashboard',
	'landing_page',
	'sentry',
];

// ---------------------------------------------------------------------------
// Time-range presets
// ---------------------------------------------------------------------------

type TimeRange = '24h' | '7d' | '30d';

const RANGE_HOURS: Record<TimeRange, number> = {
	'24h': 24,
	'7d': 168,
	'30d': 720,
};

const RANGE_LABELS: Record<TimeRange, string> = {
	'24h': '24 hours',
	'7d': '7 days',
	'30d': '30 days',
};

// How many timeline buckets to display per range
const RANGE_BUCKETS: Record<TimeRange, number> = {
	'24h': 48, // 30 min each
	'7d': 84, // 2 hours each
	'30d': 90, // 8 hours each
};

// ---------------------------------------------------------------------------
// Timeline helpers
// ---------------------------------------------------------------------------

interface Bucket {
	start: Date;
	end: Date;
	incidents: HealthCheck[];
	status: 'healthy' | 'degraded' | 'down';
}

/**
 * Distribute incident-only rows into time buckets.
 * Buckets default to 'healthy' — only incidents change that.
 */
function bucketize(
	incidents: HealthCheck[],
	bucketCount: number,
	rangeMs: number,
): Bucket[] {
	const now = Date.now();
	const start = now - rangeMs;
	const bucketMs = rangeMs / bucketCount;

	const buckets: Bucket[] = [];
	for (let i = 0; i < bucketCount; i++) {
		const bStart = new Date(start + i * bucketMs);
		const bEnd = new Date(start + (i + 1) * bucketMs);
		buckets.push({ start: bStart, end: bEnd, incidents: [], status: 'healthy' });
	}

	for (const incident of incidents) {
		const t = new Date(incident.checked_at).getTime();
		const idx = Math.min(
			Math.floor((t - start) / bucketMs),
			bucketCount - 1,
		);
		if (idx >= 0 && idx < bucketCount) {
			buckets[idx].incidents.push(incident);
		}
	}

	for (const bucket of buckets) {
		if (bucket.incidents.length === 0) continue;
		const hasDown = bucket.incidents.some((c) => c.status === 'down');
		if (hasDown) {
			bucket.status = 'down';
		} else {
			bucket.status = 'degraded';
		}
	}

	return buckets;
}

const BUCKET_COLORS: Record<string, string> = {
	healthy: 'bg-emerald-500',
	degraded: 'bg-amber-500',
	down: 'bg-red-500',
};

function formatTime(date: Date): string {
	return date.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

function OverallBanner({ services }: { services: Map<string, HealthCheck> }) {
	if (services.size === 0) return null;

	const statuses = Array.from(services.values()).map((c) => c.status);
	const hasDown = statuses.includes('down');
	const hasDegraded = statuses.includes('degraded');

	let bg: string;
	let text: string;
	if (hasDown) {
		bg = 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900';
		text = 'Some services are experiencing outages';
	} else if (hasDegraded) {
		bg = 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900';
		text = 'Some services are degraded';
	} else {
		bg = 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900';
		text = 'All systems operational';
	}

	return (
		<div className={`rounded-lg border p-4 ${bg}`}>
			<div className="flex items-center gap-3">
				<div className={`h-3 w-3 rounded-full ${hasDown ? 'bg-red-500' : hasDegraded ? 'bg-amber-500' : 'bg-emerald-500'}`} />
				<span className="text-sm font-medium text-zinc-900 dark:text-white">{text}</span>
			</div>
		</div>
	);
}

function ServiceTimeline({
	service,
	current,
	serviceIncidents,
	serviceStats,
	bucketCount,
	rangeMs,
}: {
	service: string;
	current: HealthCheck | undefined;
	serviceIncidents: HealthCheck[];
	serviceStats: ServiceStats | undefined;
	bucketCount: number;
	rangeMs: number;
}) {
	const meta = SERVICE_META[service] ?? { label: service, icon: <Activity className="h-4 w-4" /> };
	const buckets = useMemo(
		() => bucketize(serviceIncidents, bucketCount, rangeMs),
		[serviceIncidents, bucketCount, rangeMs],
	);

	const uptimePct = serviceStats ? serviceStats.uptimePct.toFixed(1) : '—';
	const avgLatency = serviceStats?.avgLatencyMs ?? null;
	const status: HealthStatus = (current?.status as HealthStatus) ?? 'down';

	return (
		<div className="rounded-lg border border-zinc-950/5 bg-white p-4 dark:border-white/10 dark:bg-zinc-900">
			{/* Header row */}
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2.5">
					<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
						{meta.icon}
					</div>
					<div>
						<span className="text-sm font-medium text-zinc-900 dark:text-white">{meta.label}</span>
					</div>
				</div>
				<div className="flex items-center gap-3">
					{avgLatency != null && (
						<span className="text-xs text-zinc-500 dark:text-zinc-400">{avgLatency}ms avg</span>
					)}
					<span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">{uptimePct}%</span>
					<div className="flex items-center gap-1.5">
						<div className={`h-2 w-2 rounded-full ${STATUS_DOT[status]}`} />
						<Badge color={STATUS_BADGE_COLOR[status] as any}>{STATUS_LABEL[status]}</Badge>
					</div>
				</div>
			</div>

			{/* Timeline strip */}
			<div className="flex gap-px">
				{buckets.map((bucket, i) => {
					const incidentCount = bucket.incidents.length;
					const tooltip = incidentCount > 0
						? `${formatTime(bucket.start)} — ${formatTime(bucket.end)}\n${incidentCount} incident${incidentCount !== 1 ? 's' : ''}: ${bucket.status}`
						: `${formatTime(bucket.start)} — ${formatTime(bucket.end)}\nHealthy`;
					return (
						<div
							key={i}
							className={`h-8 flex-1 rounded-sm ${BUCKET_COLORS[bucket.status]} transition-all hover:opacity-80`}
							title={tooltip}
						/>
					);
				})}
			</div>

			{/* Error detail for current failures */}
			{current?.error_message && current.status !== 'healthy' && (
				<div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate" title={current.error_message}>
					{current.error_message}
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const StatusPage: React.FC = () => {
	const [range, setRange] = useState<TimeRange>('24h');
	const { services, loading: currentLoading, error: currentError } = useHealthChecksCurrent();
	const {
		incidents,
		stats,
		loading: historyLoading,
		error: historyError,
	} = useHealthChecksHistory(RANGE_HOURS[range], SERVICE_ORDER);

	// Group incidents by service
	const incidentsByService = useMemo(() => {
		const map = new Map<string, HealthCheck[]>();
		for (const check of incidents) {
			const list = map.get(check.service_name) ?? [];
			list.push(check);
			map.set(check.service_name, list);
		}
		return map;
	}, [incidents]);

	// Determine which services to display: union of current + known services, in preferred order
	const visibleServices = useMemo(() => {
		const known = new Set<string>();
		services.forEach((_, k) => known.add(k));
		// Also include any services that appear in incidents
		incidentsByService.forEach((_, k) => known.add(k));
		// Sort by preferred order, then alphabetically for any unknown
		return SERVICE_ORDER.filter((s) => known.has(s)).concat(
			Array.from(known).filter((s) => !SERVICE_ORDER.includes(s)).sort(),
		);
	}, [services, incidentsByService]);

	const rangeMs = RANGE_HOURS[range] * 3600_000;
	const bucketCount = RANGE_BUCKETS[range];

	const loading = currentLoading || historyLoading;
	const error = currentError || historyError;

	// Summary stats
	const totalServices = visibleServices.length;
	const healthyCount = visibleServices.filter((s) => services.get(s)?.status === 'healthy').length;
	const lastChecked = services.size > 0
		? new Date(
			Math.max(...Array.from(services.values()).map((c) => new Date(c.checked_at).getTime())),
		).toLocaleTimeString()
		: null;

	return (
		<div className="p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<Heading level={1}>Service Status</Heading>
					{lastChecked && (
						<Text className="mt-1">
							Last checked at {lastChecked} &middot; {healthyCount}/{totalServices} services healthy
						</Text>
					)}
				</div>
				<div className="flex items-center gap-1 rounded-lg border border-zinc-950/5 bg-white p-1 dark:border-white/10 dark:bg-zinc-900">
					{(['24h', '7d', '30d'] as TimeRange[]).map((r) => (
						<button
							key={r}
							onClick={() => setRange(r)}
							className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
								range === r
									? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
									: 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
							}`}
						>
							{RANGE_LABELS[r]}
						</button>
					))}
				</div>
			</div>

			{/* Error banner */}
			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
					<Text className="text-red-700 dark:text-red-300 text-sm">{error}</Text>
				</div>
			)}

			{/* Overall status banner */}
			{!loading && <OverallBanner services={services} />}

			{/* Loading state */}
			{loading && visibleServices.length === 0 && (
				<div className="text-center py-12">
					<Text>Loading service status...</Text>
				</div>
			)}

			{/* No data state */}
			{!loading && visibleServices.length === 0 && (
				<div className="text-center py-12">
					<Text>No health check data available yet. The background monitor will start collecting data shortly.</Text>
				</div>
			)}

			{/* Service timeline rows */}
			<div className="space-y-3">
				{visibleServices.map((service) => (
					<ServiceTimeline
						key={service}
						service={service}
						current={services.get(service)}
						serviceIncidents={incidentsByService.get(service) ?? []}
						serviceStats={stats.get(service)}
						bucketCount={bucketCount}
						rangeMs={rangeMs}
					/>
				))}
			</div>

			{/* Legend */}
			{visibleServices.length > 0 && (
				<div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
					<span className="font-medium">Legend:</span>
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
						<span>Healthy</span>
					</div>
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-500" />
						<span>Degraded</span>
					</div>
					<div className="flex items-center gap-1.5">
						<span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500" />
						<span>Down</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default StatusPage;
